import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  getDocs, 
  getDoc,
  query, 
  where, 
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../firebase/config';

const TOYS_COLLECTION = 'toys';
const WISHLIST_COLLECTION = 'wishlist';
const MASTER_DATA_COLLECTION = 'masterData';
const CUSTOM_MASTER_DATA_COLLECTION = 'customMasterData';

// Toy CRUD operations
export const toyService = {
  // Create a new toy
  async createToy(toyData, userId) {
    try {
      console.log('=== CREATE TOY SERVICE DEBUG ===');
      console.log('Input toyData:', JSON.stringify(toyData, null, 2));
      console.log('UserId:', userId);
      
      // Validate input data
      if (!toyData) {
        throw new Error('toyData is required');
      }
      if (!userId) {
        throw new Error('userId is required');
      }
      
      // Deep clone and sanitize the data
      const sanitizedData = JSON.parse(JSON.stringify(toyData));
      
      const toyWithUser = {
        ...sanitizedData,
        userId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      console.log('Final toyWithUser data:', JSON.stringify(toyWithUser, null, 2));
      
      const result = await addDoc(collection(db, TOYS_COLLECTION), toyWithUser);
      console.log('Create toy result:', result);
      console.log('=== CREATE TOY SERVICE END ===');
      return result;
    } catch (error) {
      console.error('=== CREATE TOY SERVICE ERROR ===');
      console.error('Error type:', error.constructor.name);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
      console.error('Input toyData:', JSON.stringify(toyData, null, 2));
      console.error('UserId:', userId);
      console.error('=== END CREATE TOY ERROR ===');
      throw error;
    }
  },

  // Get all toys for a user
  async getToys(userId) {
    const q = query(
      collection(db, TOYS_COLLECTION),
      where('userId', '==', userId)
    );
    const querySnapshot = await getDocs(q);
    const toys = querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        // Ensure all fields have default values and consistent naming
        brand: data.brand || '',
        code: data.code || '',
        name: data.name || '',
        series: data.series || '',
        type: data.type || '',
        scale: data.scale || '',
        imageUrl: data.imageUrl || '',
        qty: data.qty || 1,
        price: data.price || 0,
        preorder_price: data.preorder_price || 0,
        source: data.source || '',
        note: data.note || '-',
        createdDate: data.createdDate || ''
      };
    });
    
    // Sort by createdAt in JavaScript instead of Firestore
    return toys.sort((a, b) => {
      if (!a.createdAt || !b.createdAt) return 0;
      return b.createdAt.toDate ? b.createdAt.toDate() - a.createdAt.toDate() : 0;
    });
  },

  // Get a single toy by ID
  async getToyById(toyId) {
    try {
      console.log('=== GET TOY BY ID SERVICE DEBUG ===');
      console.log('ToyId:', toyId);
      
      if (!toyId) {
        throw new Error('Toy ID is required');
      }
      
      const docRef = doc(db, TOYS_COLLECTION, toyId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        console.log('Raw Firestore data:', JSON.stringify(data, null, 2));
        
        const processedData = { 
          id: docSnap.id, 
          ...data,
          // Ensure all fields have default values
          brand: data.brand || '',
          code: data.code || '',
          name: data.name || '',
          series: data.series || '',
          type: data.type || '',
          scale: data.scale || '',
          imageUrl: data.imageUrl || '',
          qty: data.qty || 1,
          price: data.price || 0,
          preorder_price: data.preorder_price || 0,
          source: data.source || '',
          note: data.note || '-',
          createdDate: data.createdDate || ''
        };
        
        console.log('Processed toy data:', JSON.stringify(processedData, null, 2));
        console.log('=== GET TOY BY ID SERVICE END ===');
        return processedData;
      }
      
      console.log('Toy not found');
      console.log('=== GET TOY BY ID SERVICE END ===');
      return null;
    } catch (error) {
      console.error('=== GET TOY BY ID SERVICE ERROR ===');
      console.error('Error type:', error.constructor.name);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
      console.error('ToyId:', toyId);
      console.error('=== END GET TOY BY ID ERROR ===');
      throw error;
    }
  },

  // Update a toy
  async updateToy(toyId, toyData) {
    try {
      console.log('=== UPDATE TOY SERVICE DEBUG ===');
      console.log('ToyId:', toyId);
      console.log('Input toyData:', JSON.stringify(toyData, null, 2));
      
      // Validate input data
      if (!toyId) {
        throw new Error('toyId is required');
      }
      if (!toyData) {
        throw new Error('toyData is required');
      }
      
      // Deep clone and sanitize the data
      const sanitizedData = JSON.parse(JSON.stringify(toyData));
      
      const docRef = doc(db, TOYS_COLLECTION, toyId);
      const updateData = {
        ...sanitizedData,
        updatedAt: serverTimestamp()
      };
      
      console.log('Final updateData:', JSON.stringify(updateData, null, 2));
      
      const result = await updateDoc(docRef, updateData);
      console.log('Update toy result:', result);
      console.log('=== UPDATE TOY SERVICE END ===');
      return result;
    } catch (error) {
      console.error('=== UPDATE TOY SERVICE ERROR ===');
      console.error('Error type:', error.constructor.name);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
      console.error('ToyId:', toyId);
      console.error('Input toyData:', JSON.stringify(toyData, null, 2));
      console.error('=== END UPDATE TOY ERROR ===');
      throw error;
    }
  },

  // Delete a toy
  async deleteToy(userId, toyId) {
    try {
      console.log('=== DELETE TOY SERVICE DEBUG ===');
      console.log('UserId:', userId);
      console.log('ToyId:', toyId);
      
      // Validate input data
      if (!userId) {
        throw new Error('userId is required');
      }
      if (!toyId) {
        throw new Error('toyId is required');
      }
      
      const docRef = doc(db, TOYS_COLLECTION, toyId);
      
      // Optional: Verify the toy belongs to the user before deleting
      // This adds an extra layer of security
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const toyData = docSnap.data();
        if (toyData.userId !== userId) {
          throw new Error('Unauthorized: You can only delete your own toys');
        }
      } else {
        throw new Error('Toy not found');
      }
      
      const result = await deleteDoc(docRef);
      console.log('Delete toy result:', result);
      console.log('=== DELETE TOY SERVICE END ===');
      return result;
    } catch (error) {
      console.error('=== DELETE TOY SERVICE ERROR ===');
      console.error('Error type:', error.constructor.name);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
      console.error('UserId:', userId);
      console.error('ToyId:', toyId);
      console.error('=== END DELETE TOY ERROR ===');
      throw error;
    }
  },

  // Search toys
  async searchToys(userId, searchTerm) {
    const q = query(
      collection(db, TOYS_COLLECTION),
      where('userId', '==', userId)
    );
    const querySnapshot = await getDocs(q);
    const toys = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Sort by createdAt in JavaScript
    const sortedToys = toys.sort((a, b) => {
      if (!a.createdAt || !b.createdAt) return 0;
      return b.createdAt.toDate ? b.createdAt.toDate() - a.createdAt.toDate() : 0;
    });
    
    if (!searchTerm) return sortedToys;
    
    return sortedToys.filter(toy => 
      toy.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      toy.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      toy.brand?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      toy.series?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }
};

// Wishlist operations
export const wishlistService = {
  // Create a new wishlist item
  async createWishItem(wishData, userId) {
    try {
      if (!wishData) {
        throw new Error('wishData is required');
      }
      if (!userId) {
        throw new Error('userId is required');
      }
      
      const sanitizedData = JSON.parse(JSON.stringify(wishData));
      
      // Ensure we don't have any existing createdAt or updatedAt fields that might be Date objects
      delete sanitizedData.createdAt;
      delete sanitizedData.updatedAt;
      
      const wishWithUser = {
        ...sanitizedData,
        userId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      console.log('Creating wishlist item with serverTimestamp:', {
        userId,
        createdAt: 'serverTimestamp()',
        updatedAt: 'serverTimestamp()'
      });
      
      const result = await addDoc(collection(db, WISHLIST_COLLECTION), wishWithUser);
      return result;
    } catch (error) {
      console.error('Error creating wish item:', error);
      throw error;
    }
  },

  // Get all wishlist items for a user
  async getWishItems(userId) {
    const q = query(
      collection(db, WISHLIST_COLLECTION),
      where('userId', '==', userId)
    );
    const querySnapshot = await getDocs(q);
    const wishItems = querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        brand: data.brand || '',
        code: data.code || '',
        name: data.name || '',
        series: data.series || '',
        type: data.type || '',
        scale: data.scale || '',
        imageUrl: data.imageUrl || '',
        price: data.price || 0,
        note: data.note || '-',
        createdDate: data.createdDate || ''
      };
    });
    
    // Check if any items have Date objects and fix them
    const itemsWithDateObjects = wishItems.filter(item => 
      item.createdAt && !item.createdAt.toDate && item.createdAt instanceof Date
    );
    
    if (itemsWithDateObjects.length > 0) {
      console.log(`Found ${itemsWithDateObjects.length} wishlist items with Date objects, fixing...`);
      await this.fixWishlistTimestamps(userId);
      
      // Re-fetch the items after fixing
      const updatedQuerySnapshot = await getDocs(q);
      const updatedWishItems = updatedQuerySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          brand: data.brand || '',
          code: data.code || '',
          name: data.name || '',
          series: data.series || '',
          type: data.type || '',
          scale: data.scale || '',
          imageUrl: data.imageUrl || '',
          price: data.price || 0,
          note: data.note || '-',
          createdDate: data.createdDate || ''
        };
      });
      
      return updatedWishItems.sort((a, b) => {
        if (!a.createdAt || !b.createdAt) return 0;
        
        // Handle both Firestore timestamps and Date objects
        const aDate = a.createdAt.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
        const bDate = b.createdAt.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
        
        return bDate - aDate;
      });
    }
    
    return wishItems.sort((a, b) => {
      if (!a.createdAt || !b.createdAt) return 0;
      
      // Handle both Firestore timestamps and Date objects
      const aDate = a.createdAt.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
      const bDate = b.createdAt.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
      
      return bDate - aDate;
    });
  },

  // Get a single wishlist item by ID
  async getWishItemById(wishId) {
    const docRef = doc(db, WISHLIST_COLLECTION, wishId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        id: docSnap.id,
        ...data,
        brand: data.brand || '',
        code: data.code || '',
        name: data.name || '',
        series: data.series || '',
        type: data.type || '',
        scale: data.scale || '',
        imageUrl: data.imageUrl || '',
        price: data.price || 0,
        note: data.note || '-',
        createdDate: data.createdDate || ''
      };
    } else {
      throw new Error('Wish item not found');
    }
  },

  // Update a wishlist item
  async updateWishItem(wishId, wishData) {
    try {
      const sanitizedData = JSON.parse(JSON.stringify(wishData));
      
      // Ensure we don't have any existing createdAt or updatedAt fields that might be Date objects
      delete sanitizedData.createdAt;
      delete sanitizedData.updatedAt;
      
      const updateData = {
        ...sanitizedData,
        updatedAt: serverTimestamp()
      };
      
      console.log('Updating wishlist item with serverTimestamp:', {
        wishId,
        updatedAt: 'serverTimestamp()'
      });
      
      const docRef = doc(db, WISHLIST_COLLECTION, wishId);
      const result = await updateDoc(docRef, updateData);
      return result;
    } catch (error) {
      console.error('Error updating wish item:', error);
      throw error;
    }
  },

  // Delete a wishlist item
  async deleteWishItem(wishId) {
    const docRef = doc(db, WISHLIST_COLLECTION, wishId);
    return await deleteDoc(docRef);
  },

  // Convert wishlist item to toy (and delete from wishlist)
  async convertWishToToy(wishId, userId) {
    try {
      // Get the wish item
      const wishItem = await this.getWishItemById(wishId);
      
      // Create a new toy from the wish item
      const toyData = {
        ...wishItem,
        // Add default values for toy-specific fields
        qty: 1,
        source: '',
        // Remove wishlist-specific fields
        id: undefined,
        createdAt: undefined,
        updatedAt: undefined
      };
      
      // Create the toy
      await toyService.createToy(toyData, userId);
      
      // Delete the wish item
      await this.deleteWishItem(wishId);
      
      return true;
    } catch (error) {
      console.error('Error converting wish to toy:', error);
      throw error;
    }
  },

  // Search wishlist items
  async searchWishItems(userId, searchTerm) {
    const q = query(
      collection(db, WISHLIST_COLLECTION),
      where('userId', '==', userId)
    );
    const querySnapshot = await getDocs(q);
    const wishItems = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    const sortedItems = wishItems.sort((a, b) => {
      if (!a.createdAt || !b.createdAt) return 0;
      
      // Handle both Firestore timestamps and Date objects
      const aDate = a.createdAt.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
      const bDate = b.createdAt.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
      
      return bDate - aDate;
    });
    
    if (!searchTerm) return sortedItems;
    
    return sortedItems.filter(item => 
      item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.brand?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.series?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  },

  // Fix existing wishlist items that have Date objects instead of Firestore timestamps
  async fixWishlistTimestamps(userId) {
    try {
      console.log('Starting wishlist timestamp fix for user:', userId);
      
      const q = query(
        collection(db, WISHLIST_COLLECTION),
        where('userId', '==', userId)
      );
      const querySnapshot = await getDocs(q);
      
      let fixedCount = 0;
      
      for (const doc of querySnapshot.docs) {
        const data = doc.data();
        
        // Check if createdAt is a Date object (not a Firestore timestamp)
        if (data.createdAt && !data.createdAt.toDate && data.createdAt instanceof Date) {
          console.log('Fixing wishlist item with Date object:', doc.id);
          
          // Update the document to use serverTimestamp
          const docRef = doc(db, WISHLIST_COLLECTION, doc.id);
          await updateDoc(docRef, {
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          });
          
          fixedCount++;
        }
      }
      
      console.log(`Fixed ${fixedCount} wishlist items for user ${userId}`);
      return fixedCount;
    } catch (error) {
      console.error('Error fixing wishlist timestamps:', error);
      throw error;
    }
  }
};

// Master data operations
export const masterDataService = {
  // Get master data for autocomplete (includes both global and user-specific data)
  async getMasterData(userId = null) {
    const masterData = {
      brands: [],
      series: [],
      types: [],
      scales: [],
      sources: []
    };
    
    // Get global master data
    const globalQuerySnapshot = await getDocs(collection(db, MASTER_DATA_COLLECTION));
    globalQuerySnapshot.docs.forEach(doc => {
      const data = doc.data();
      if (data.type && data.values) {
        masterData[data.type] = [...(masterData[data.type] || []), ...data.values];
      }
    });
    
    // Get user-specific custom data if userId is provided
    if (userId) {
      const customQuerySnapshot = await getDocs(
        query(
          collection(db, CUSTOM_MASTER_DATA_COLLECTION),
          where('userId', '==', userId)
        )
      );
      
      customQuerySnapshot.docs.forEach(doc => {
        const data = doc.data();
        if (data.type && data.value) {
          if (!masterData[data.type]) {
            masterData[data.type] = [];
          }
          // Add custom value if it's not already in the list
          if (!masterData[data.type].includes(data.value)) {
            masterData[data.type].push(data.value);
          }
        }
      });
    }
    
    // Remove duplicates and sort
    Object.keys(masterData).forEach(key => {
      masterData[key] = [...new Set(masterData[key])].sort();
    });
    
    return masterData;
  },

  // Get only custom master data for a specific user
  async getCustomMasterData(userId) {
    if (!userId) return { brands: [], series: [], types: [], scales: [], sources: [] };
    
    const customData = {
      brands: [],
      series: [],
      types: [],
      scales: [],
      sources: []
    };
    
    const querySnapshot = await getDocs(
      query(
        collection(db, CUSTOM_MASTER_DATA_COLLECTION),
        where('userId', '==', userId)
      )
    );
    
    querySnapshot.docs.forEach(doc => {
      const data = doc.data();
      if (data.type && data.value) {
        customData[data.type].push(data.value);
      }
    });
    
    // Sort each array
    Object.keys(customData).forEach(key => {
      customData[key].sort();
    });
    
    return customData;
  },

  // Add custom master data for a specific user
  async addCustomMasterData(userId, type, value) {
    if (!userId || !type || !value) {
      throw new Error('userId, type, and value are required');
    }
    
    // Check if this custom value already exists for this user
    const existingQuery = query(
      collection(db, CUSTOM_MASTER_DATA_COLLECTION),
      where('userId', '==', userId),
      where('type', '==', type),
      where('value', '==', value)
    );
    
    const existingSnapshot = await getDocs(existingQuery);
    if (!existingSnapshot.empty) {
      console.log(`Custom ${type} "${value}" already exists for user ${userId}`);
      return existingSnapshot.docs[0].id;
    }
    
    // Add the custom value
    const result = await addDoc(collection(db, CUSTOM_MASTER_DATA_COLLECTION), {
      userId,
      type,
      value: value.trim(),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    console.log(`Added custom ${type}: "${value}" for user ${userId}`);
    return result.id;
  },

  // Delete custom master data for a specific user
  async deleteCustomMasterData(userId, type, value) {
    if (!userId || !type || !value) {
      throw new Error('userId, type, and value are required');
    }
    
    const querySnapshot = await getDocs(
      query(
        collection(db, CUSTOM_MASTER_DATA_COLLECTION),
        where('userId', '==', userId),
        where('type', '==', type),
        where('value', '==', value)
      )
    );
    
    if (!querySnapshot.empty) {
      const docRef = doc(db, CUSTOM_MASTER_DATA_COLLECTION, querySnapshot.docs[0].id);
      await deleteDoc(docRef);
      console.log(`Deleted custom ${type}: "${value}" for user ${userId}`);
      return true;
    }
    
    return false;
  },

  // Initialize master data (run once to set up)
  async initializeMasterData() {
    const masterData = {
      brands: ['Bandai', 'Good Smile Company', 'Kotobukiya', 'Max Factory', 'MegaHouse'],
      series: ['30MS', '30MM', 'Figma', 'Nendoroid', 'Scale Figure'],
      types: ['Girlpla', 'Model Kit', 'Figure', 'Nendoroid', 'Scale Figure'],
      scales: ['1/12', '1/7', '1/8', '1/6', '1/4', '1/1'],
      sources: ['VAC', 'AmiAmi', 'HobbyLink Japan', 'HLJ', 'Mandarake']
    };

    for (const [key, values] of Object.entries(masterData)) {
      await addDoc(collection(db, MASTER_DATA_COLLECTION), {
        type: key,
        values: values
      });
    }
  }
}; 